/* eslint-disable react-hooks/exhaustive-deps */

import {
  I2cInput,
  I2cTextarea,
  I2cDatePicker,
  I2cPhoneField,
} from "@webcomponents/i2cwebcomponents/dist/react";
import React, { useEffect } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { isValidUrl } from "../../../Utils/stringUtls";

/**
 * Component for capturing and validating project details.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.setFormValidStates - Function to set form validation states.
 * @returns {React.Component} - The rendered component.
 */
const CompanyInformation = ({
  setFormValidStates,
  setAddFormData,
  addFormData,
  formDataJson,
  isFetching,
}) => {
  // Define validation schema using Zod
  const validationSchema = z.object({
    ci_businessName: z
      .string()
      .optional(),
    ci_physicalAddress: z
      .string()
      .optional(),
    ci_mailingAddress: z
      .string()
      .optional(), 
    ci_companyPhone: z
      .string()
      .optional(),
    ci_companyWebURL: z
      .string()
      .optional()
      .refine((val) => !val || isValidUrl(val), { message: "Invalid URL" }), 
    ci_otherBusinessLoc: z.string().optional(), 
    ci_date_business_established: z
      .string()
      .optional(), 
  });

  // Initialize React Hook Form with Zod resolver
  const methods = useForm({
    resolver: zodResolver(validationSchema),
    mode: "all",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "firstError",
    defaultValues: {
      ci_businessName: "",
      ci_physicalAddress: "",
      ci_mailingAddress: "",  
      ci_companyPhone: "",
      ci_companyPhoneCountry: "US",
      ci_companyWebURL: "",
      ci_otherBusinessLoc: "",
      ci_date_business_established: "",
    },
  });

  const {
    control,
    formState: { errors },
  } = methods;

  const [dateBusinessEstablishedDefault, setDateBusinessEstablishedDefault] = React.useState("");

  const fields = [
    "ci_businessName",
    "ci_physicalAddress",
    "ci_mailingAddress", 
    "ci_companyPhone",
    "ci_companyPhoneCountry",
    "ci_companyWebURL",
    "ci_otherBusinessLoc",
    "ci_date_business_established",
  ];
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        methods.setValue(field, formDataJson[0]["0_2"][field] ?? "");
      });
      const dateVal = formDataJson[0]["0_2"]["ci_date_business_established"] ?? "";
      if (dateVal) setDateBusinessEstablishedDefault(new Date(dateVal));
      methods.trigger();
      handleValueChanges();
    }
  }, [isFetching]);
  // Trigger validation when component mounts
  React.useEffect(() => {
    methods.trigger();
  }, []);
 
  
  const handleValueChanges = () => {
    const values = methods.getValues();
    setAddFormData((prev) => ({
      ...prev,
      0: {
        ...prev["0"],
        "0_2": { ...values },
      },
    }));
  };
  // Update form validation state when errors change
  React.useEffect(() => {
    const errorsLength = Object.keys(errors).length;
    if (errorsLength === 0) setFormValidStates("0_2", true);
    else setFormValidStates("0_2", false);
  }, [Object.keys(errors).length]);
  // useEffect(() => {
  //   const errorsLength = Object.keys(errors).length;
  //   setFormValidStates("0_2", errorsLength === 0);
  // }, [Object.keys(errors).length, setFormValidStates]);
 
  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-column-sm gap-row-md">
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_businessName"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="Trade or Business Name"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Trade or Business Name"
                    name={"ci_businessName"}
                    size="large"
                  />
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.ci_businessName &&
                  methods.formState.touchedFields.ci_businessName
                    ? true
                    : false
                }
                message={errors.ci_businessName?.message ?? "Invalid input"}
              /> */}
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <I2cPhoneField
                format="NATIONAL"
                label="Company Phone "
                show-flags
                tooltip-help="Contact Number"
                name="ci_companyPhone"
                size="large"
                country-box
                helpText=" "
                selectedCountry={methods.watch("ci_companyPhoneCountry")}
                errorMessageVisible={false}
                value={methods.watch("ci_companyPhone")}
                onI2cChange={(e) => { 
                  const country = e.target.country;
                  const isValid = e.target.isValid;
                  const value = e.target.value;
                  methods.setValue("ci_companyPhoneCountry", country);
                  methods.setValue("ci_companyPhone", value);
                  handleValueChanges();
                  if (!isValid) {
                    methods.setError("ci_companyPhone", {
                      message: e.target.guide,
                    });
                  } else {
                    methods.clearErrors("ci_companyPhone");
                  }
                  // if (isValid) {
                  // 	methods.setValue("ci_companyPhone", value);
                  // 	handleValueChanges();
                  // } else {
                  // 	methods.setError("ci_companyPhone", {
                  // 		message: e.target.guide,
                  // 	});
                  // }
                }}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_physicalAddress"
                control={control}
                render={({ field }) => (
                  <I2cTextarea
                    {...field}
                    className="single-input-field"
                    placeholder="Enter Physical Address"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Physical Address"
                    name={"ci_physicalAddress"}
                    rows="2"
                    draggable={true}
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.ci_physicalAddress &&
                    methods.formState.touchedFields.ci_physicalAddress
                    ? true
                    : false
                }
                message={errors.ci_physicalAddress?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_mailingAddress"
                control={control}
                render={({ field }) => (
                  <I2cTextarea
                    {...field}
                    className="single-input-field"
                    placeholder="Enter Mailing Address"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Mailing Address"
                    name={"ci_mailingAddress"}
                    rows="2"
                    draggable={true}
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.ci_mailingAddress &&
                    methods.formState.touchedFields.ci_mailingAddress
                    ? true
                    : false
                }
                message={errors.ci_mailingAddress?.message ?? "Invalid input"}
              />
            </div>
          </div> 
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_companyWebURL"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="https://domain.com"
                    size="large"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    type="text"
                    label="Company Website URL"
                    name={"ci_companyWebURL"}
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.ci_companyWebURL &&
                  methods.formState.touchedFields.ci_companyWebURL
                    ? true
                    : false
                }
                message={errors.ci_companyWebURL?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_otherBusinessLoc"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="Other Business Locations"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Other Business Locations"
                    name={"ci_otherBusinessLoc"}
                    size="large"
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.ci_otherBusinessLoc &&
                    methods.formState.touchedFields.ci_otherBusinessLoc
                    ? true
                    : false
                }
                message={errors.ci_otherBusinessLoc?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_date_business_established"
                control={control}
                render={({ field }) => (
                  <>
                    <I2cDatePicker
                      {...field}
                      rotateIcon
                      name="ci_date_business_established"
                      suffix='<i2c-icon name="linear-calendar-2" slot="suffix" class="suffix-iCon4"></i2c-icon>'
                      hoist
                      onI2cDateApply={(e) => {
                        if (e.target?.appliedDate) {
                          const dateStr = e.target?.appliedDate[0]?.toDateString() ?? "";
                          field.onChange(dateStr);
                          setDateBusinessEstablishedDefault(dateStr ? new Date(dateStr) : "");
                        } else {
                          field.onChange("");
                          setDateBusinessEstablishedDefault("");
                        }
                        handleValueChanges();
                      }}
                      I2cDatePicker
                      dateFormat="F d, Y"
                      size="large"
                      label="Date Business Established"
                      // className="custom-dt-picker"
                      errorMessageInvisible={true}
                      errorMessageVisible={false}
                      defaultDate={dateBusinessEstablishedDefault}
                    />
                    {/* <span slot="label"></span> */}
                  </>
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.ci_date_business_established &&
                  methods.formState.touchedFields.ci_date_business_established
                    ? true
                    : false
                }
                message={
                  errors.ci_date_business_established?.message ??
                  "Invalid input"
                }
              /> */}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CompanyInformation;
